import React, { useEffect, useMemo, useState } from 'react';
import { fetchHinduFestivalList } from '../../../services/hinduFestivalService';

const PAGE_SIZE = 100;

const formatFestivalLabel = (row) => {
  if (!row) return 'Unknown festival';
  const name = row.name || 'Untitled';
  const date = row.date || row.start_date || '—';
  const range =
    row.start_date && row.end_date && row.start_date !== row.end_date
      ? `${row.start_date} → ${row.end_date}`
      : row.start_date || '';
  const inactive = row.is_active === false ? ' (inactive)' : '';
  const missing = row._missing ? ' (missing / not in list)' : '';
  return `${name} · ${date}${range ? ` · ${range}` : ''}${inactive}${missing}`;
};

/** Multi-day festival: both start and end present and different (YYYY-MM-DD). */
export const isMultiDayFestival = (row) => {
  if (!row || row._missing) return false;
  const start = String(row.start_date || '').trim();
  const end = String(row.end_date || '').trim();
  return Boolean(start && end && start !== end);
};

/** Single-day festival: start === end, or only one date available. */
export const isSingleDayFestival = (row) => {
  if (!row || row._missing) return false;
  const start = String(row.start_date || '').trim();
  const end = String(row.end_date || '').trim();
  if (start && end) return start === end;
  return Boolean(start || end || row.date);
};

/**
 * Single-select Hindu Festival for special_date_range / special_date_set.
 * Dates resolve by Mongo _id only — no name/type inference.
 * dateFilter: "range" = multi-day only; "single" = single-day only; "all" = no date-shape filter.
 * List API has no is_active filter; inactive rows are labeled and blocked for new picks.
 * selectedIds is still an array (Backend contract); UI keeps at most one id when picking.
 * Legacy multi-id / wrong-shape saved IDs stay visible until Admin repairs.
 */
const HinduFestivalMultiSelect = ({
  selectedIds = [],
  onChange,
  disabled = false,
  error = '',
  dateFilter = 'all',
}) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [festivals, setFestivals] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const res = await fetchHinduFestivalList(1, PAGE_SIZE, debouncedSearch);
        if (cancelled) return;
        const items = Array.isArray(res?.data) ? res.data : [];
        setFestivals(items);
      } catch (err) {
        if (cancelled) return;
        setFestivals([]);
        setLoadError(err.message || 'Failed to load Hindu Festivals');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch]);

  const byId = useMemo(() => {
    const map = new Map();
    for (const row of festivals) {
      if (row?._id) map.set(String(row._id), row);
    }
    return map;
  }, [festivals]);

  const selectedSet = useMemo(
    () => new Set((selectedIds || []).map((id) => String(id))),
    [selectedIds]
  );

  const multiLegacy = (selectedIds || []).length > 1;

  /**
   * Keep list order stable (API / search order). Do not move selected rows to top.
   * Apply dateFilter for pickable rows; append saved IDs missing from filtered list at end.
   */
  const displayRows = useMemo(() => {
    const filtered = festivals.filter((row) => {
      if (dateFilter === 'range') return isMultiDayFestival(row);
      if (dateFilter === 'single') return isSingleDayFestival(row);
      return true;
    });
    const rows = [...filtered];
    const seen = new Set(rows.map((r) => String(r._id)));
    for (const id of selectedIds || []) {
      const sid = String(id);
      if (seen.has(sid)) continue;
      const known = byId.get(sid) || festivals.find((r) => String(r._id) === sid);
      if (known) {
        rows.push(known);
        seen.add(sid);
      } else {
        rows.push({
          _id: sid,
          name: `Festival ${sid.slice(0, 8)}…`,
          date: '—',
          is_active: false,
          _missing: true,
        });
        seen.add(sid);
      }
    }
    return rows;
  }, [festivals, selectedIds, byId, dateFilter]);

  const selectOne = (row) => {
    if (disabled || !row?._id) return;
    const id = String(row._id);
    const already = selectedSet.has(id);
    if (!already && row.is_active === false && !row._missing) {
      // Block newly selecting inactive festivals; keep already-saved inactive for repair.
      return;
    }
    if (!already && row._missing) return;
    // Single-select: click selected → clear; click another → replace with that id only.
    onChange?.(already ? [] : [id]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <label className="block text-xs font-medium text-gray-700">
          Hindu Festival <span className="text-red-500">*</span>
        </label>
        <span className="text-[11px] text-gray-400">
          {selectedIds?.length || 0} selected
        </span>
      </div>
      <input
        type="search"
        value={search}
        disabled={disabled}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search festivals by name or date…"
        className="w-full h-10 px-3 text-sm border border-[#ECECEC] rounded-xl focus:outline-none bg-white disabled:opacity-50"
        aria-label="Search Hindu Festivals"
      />
      <p className="text-[11px] text-gray-400">
        {dateFilter === 'range'
          ? 'Showing multi-day festivals only (start date different from end date). '
          : dateFilter === 'single'
            ? 'Showing single-day festivals only. '
            : ''}
        Select exactly one Learning → Hindu Festivals record. Dates come from that row
        (start→end). Inactive festivals cannot be newly selected; already saved IDs stay
        visible so you can clear or replace them.
      </p>
      {multiLegacy ? (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
          This badge has multiple festivals saved. Choose one festival below (or clear and
          re-select) before saving — only one ID is allowed.
        </p>
      ) : null}
      {loading ? (
        <p className="text-xs text-gray-500" role="status">
          Loading festivals…
        </p>
      ) : null}
      {loadError ? <p className="text-xs text-red-600 whitespace-pre-line">{loadError}</p> : null}
      <div
        className="max-h-48 overflow-y-auto rounded-xl border border-[#ECECEC] bg-white divide-y divide-gray-100"
        role="radiogroup"
        aria-label="Hindu Festival single-select"
      >
        {!loading && !displayRows.length ? (
          <p className="px-3 py-3 text-xs text-gray-500">No festivals found.</p>
        ) : null}
        {displayRows.map((row) => {
          const id = String(row._id);
          const checked = selectedSet.has(id);
          const inactive = row.is_active === false;
          const blockNew = inactive && !checked && !row._missing;
          return (
            <label
              key={id}
              className={`flex items-start gap-2 px-3 py-2 text-sm ${
                blockNew || disabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer hover:bg-gray-50'
              } ${checked ? 'bg-gray-50' : ''}`}
            >
              <input
                type="radio"
                name="hindu-festival-single"
                className="mt-0.5"
                checked={checked}
                disabled={disabled || blockNew}
                onChange={() => selectOne(row)}
                onClick={() => {
                  // Allow clearing the current selection by clicking the selected radio.
                  if (checked && !disabled && !blockNew) selectOne(row);
                }}
                aria-label={formatFestivalLabel(row)}
              />
              <span className="min-w-0 break-words text-gray-800">
                {formatFestivalLabel(row)}
                {blockNew ? (
                  <span className="block text-[11px] text-amber-700">
                    Inactive — cannot select for new configs
                  </span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
      {error ? <p className="text-xs text-red-600 whitespace-pre-line">{error}</p> : null}
    </div>
  );
};

export default HinduFestivalMultiSelect;
export { formatFestivalLabel };
